import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2021, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef, useState } from 'react';
import { css } from '@emotion/react';
import { some } from 'lodash/fp';
import styled from '../../styles/styled';
import { hideVisually } from '../../styles/style-mixins';
import { warn } from '../../util/logger';
import Body from '../Body';
import ListItem from '../ListItem';
const baseStyles = css `
  display: flex;
  flex-direction: column;
`;
const StyledListItemGroup = styled.div(baseStyles);
const headerContainerStyles = ({ theme }) => css `
  flex: none;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin: 0 ${theme.spacings.mega};
`;
const headerContainerPlainStyles = ({ theme, isPlain, }) => isPlain &&
    css `
    margin: 0 calc(${theme.spacings.mega} - ${theme.borderWidth.mega});
  `;
const HeaderContainer = styled.div(headerContainerStyles, headerContainerPlainStyles);
const labelContainerStyles = ({ theme }) => css `
  flex: auto;
  min-width: 0;
  margin-bottom: ${theme.spacings.byte};
`;
const labelContainerHiddenStyles = ({ hideLabel }) => hideLabel && hideVisually();
const LabelContainer = styled.div(labelContainerStyles, labelContainerHiddenStyles);
const labelStyles = css `
  max-width: 100%;
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const Label = styled(Body)(labelStyles);
const detailsContainerStyles = ({ theme }) => css `
  flex: none;
  margin-left: ${theme.spacings.mega};
  margin-bottom: ${theme.spacings.byte};
`;
const DetailsContainer = styled.div(detailsContainerStyles);
const itemsContainerBaseStyles = ({ theme }) => css `
  flex: auto;
  display: flex;
  flex-direction: column;
  border: ${theme.borderWidth.mega} solid ${theme.colors.n200};
  border-radius: ${theme.borderRadius.mega};
`;
const itemsContainerPlainStyles = ({ theme, isPlain, }) => isPlain &&
    css `
    border-width: ${theme.borderWidth.kilo} 0;
    border-radius: 0;
  `;
const ItemsContainer = styled.ul(itemsContainerBaseStyles, itemsContainerPlainStyles);
const liStyles = ({ theme }) => css `
  list-style: none;

  &:not(:first-of-type) > * > div:last-of-type {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: -${theme.spacings.kilo};
      left: 0;
      right: -${theme.spacings.mega};
      border-top: ${theme.borderWidth.kilo} solid ${theme.colors.n200};
    }
  }
`;
const liInteractiveStyles = ({ isInteractive }) => isInteractive &&
    css `
    &:hover {
      &,
      & + li {
        &:not(:first-of-type) > * > div:last-of-type:before {
          border-top-width: 0;
        }
      }
    }
  `;
const liFocusedStyles = ({ isFocused }) => isFocused &&
    css `
    &,
    & + li {
      &:not(:first-of-type) > * > div:last-of-type:before {
        border-top-width: 0;
      }
    }
  `;
const liSelectedStyles = ({ selected }) => selected &&
    css `
    &,
    & + li {
      &:not(:first-of-type) > * > div:last-of-type:before {
        border-top-width: 0;
      }
    }
  `;
const StyledLi = styled.li(liStyles, liInteractiveStyles, liFocusedStyles, liSelectedStyles);
const listItemStyles = ({ theme }) => css `
  border: none;
  border-radius: calc(${theme.borderRadius.mega} - ${theme.borderWidth.mega});
`;
const listItemPlainStyles = ({ theme, isPlain, selected, }) => isPlain &&
    selected &&
    css `
    &:after {
      top: -${theme.borderWidth.kilo};
      bottom: -${theme.borderWidth.kilo};
      left: 0;
      right: 0;
    }
  `;
const StyledListItem = styled(ListItem)(listItemStyles, listItemPlainStyles);
/**
 * The ListItemGroup component enables the user to render a named list of ListItem components.
 */
export const ListItemGroup = forwardRef(({ variant = 'inset', items, label, hideLabel, details, ...props }, ref) => {
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label) {
        warn('ListItemGroup', 'The `label` prop is missing. This is an accessibility requirement. Pass `hideLabel` if you intend to hide the label visually.');
    }
    const [focusedItemKey, setFocusedItemKey] = useState(null);
    const isPlain = variant === 'plain';
    const isInteractive = some((item) => !!item.href || !!item.onClick, items);
    return (_jsxs(StyledListItemGroup, { ...props, ref: ref, children: [_jsxs(HeaderContainer, { isPlain: isPlain, children: [_jsx(LabelContainer, { hideLabel: hideLabel, children: typeof label === 'string' ? (_jsx(Label, { as: "h4", size: "two", noMargin: true, children: label }, void 0)) : (label) }, void 0), details && (_jsx(DetailsContainer, { children: typeof details === 'string' ? (_jsx(Body, { size: "two", noMargin: true, children: details }, void 0)) : (details) }, void 0))] }, void 0), _jsx(ItemsContainer, { isPlain: isPlain, children: items.map(({ key, ...item }) => (_jsx(StyledLi, { isFocused: focusedItemKey === key, isInteractive: isInteractive, selected: item.selected, children: _jsx(StyledListItem, { ...item, isPlain: isPlain, "aria-pressed": item.onClick ? item.selected : undefined, onFocus: (event) => {
                            try {
                                if (event.currentTarget.matches(':focus-visible')) {
                                    setFocusedItemKey(key);
                                }
                            }
                            catch (err) {
                                setFocusedItemKey(key);
                            }
                        }, onBlur: () => setFocusedItemKey(null) }, void 0) }, key))) }, void 0)] }, void 0));
});
ListItemGroup.displayName = 'ListItemGroup';
